// Coordinator Synthesis — Isomorphism §7.3 Pattern 2 (audit feedback routing).
//
// Phase 3.5 output: cross-stage semantic synthesis by a dedicated LLM call.
// The coordinator READS all stage summaries and PRODUCES structured recommendations.
// It does NOT modify any pipeline data — its output is advisory.
//
// Design rationale (CLAUDE-CODE-ARCHITECTURE-ISOMORPHISM.md §7):
//   Claude Code's Coordinator must "understand, not forward".
//   Our staged-orchestrator currently collects stage outputs mechanically.
//   Phase 3.5 adds the "understanding" layer: one DeepSeek call reviews
//   all stage summaries for cross-stage consistency, gaps, and duplicates.
//
// Cost: ~5-10K input + 2-5K output per call = ¥0.01-0.02.
// Runs once per ingest, after all Phase 3 stages complete.
import { z } from 'zod';
import { StageIdSchema } from './stage-id.js';
/** Assessment of a concept's coverage across stages. */
export const CrossStageConsistencySchema = z.object({
    /** Concept id from the CurriculumOutline anchor. */
    conceptId: z.string(),
    /** Which stages produced items covering this concept. */
    stages: z.array(z.string()),
    /** Consistency assessment. */
    assessment: z.enum(['consistent', 'inconsistent', 'missing']),
    /** Optional free-form note explaining the assessment. */
    note: z.string().optional(),
});
/** Detected duplicate items across stages (same concept, similar content). */
export const DuplicateItemSchema = z.object({
    /** The concept that appears duplicated. */
    conceptId: z.string(),
    /** First stage where it appears. */
    stageA: z.string(),
    /** Second stage where it appears. */
    stageB: z.string(),
    /** Brief description of what overlaps. */
    overlap: z.string().optional(),
});
/** Coordinator recommendation for a specific stage/chapter. */
export const RoutingSuggestionSchema = z.object({
    /** Stage to act on. */
    stageId: StageIdSchema,
    /** Optional chapter scope (omit for course-wide suggestions). */
    chapterId: z.string().optional(),
    /** Recommended action. */
    action: z.enum(['rerun', 'accept', 'add_items']),
    /** Human-readable reason for the recommendation. */
    reason: z.string(),
});
/** Coordinator Synthesis output — the full Phase 3.5 result. */
export const CoordinatorSynthesisSchema = z.object({
    /** Per-concept cross-stage consistency assessment. */
    crossStageConsistency: z.array(CrossStageConsistencySchema),
    /** Concepts from the anchor that no stage covered. */
    omittedConcepts: z.array(z.string()),
    /** Items that appear duplicated across stages. */
    duplicateItems: z.array(DuplicateItemSchema),
    /** Routing suggestions for the orchestrator (future: auto-rerun on 'rerun'). */
    routingSuggestions: z.array(RoutingSuggestionSchema),
    /** Overall quality summary — free-form LLM assessment. */
    overallAssessment: z.string(),
});
/** JSON Schema for LLM structured output. */
export const coordinatorSynthesisJsonSchema = {
    type: 'object',
    properties: {
        crossStageConsistency: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    conceptId: { type: 'string' },
                    stages: { type: 'array', items: { type: 'string' } },
                    assessment: { type: 'string', enum: ['consistent', 'inconsistent', 'missing'] },
                    note: { type: 'string' },
                },
                required: ['conceptId', 'stages', 'assessment'],
            },
        },
        omittedConcepts: {
            type: 'array',
            items: { type: 'string' },
        },
        duplicateItems: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    conceptId: { type: 'string' },
                    stageA: { type: 'string' },
                    stageB: { type: 'string' },
                    overlap: { type: 'string' },
                },
                required: ['conceptId', 'stageA', 'stageB'],
            },
        },
        routingSuggestions: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    stageId: { type: 'string' },
                    chapterId: { type: 'string' },
                    action: { type: 'string', enum: ['rerun', 'accept', 'add_items'] },
                    reason: { type: 'string' },
                },
                required: ['stageId', 'action', 'reason'],
            },
        },
        overallAssessment: { type: 'string' },
    },
    required: ['crossStageConsistency', 'omittedConcepts', 'duplicateItems', 'routingSuggestions', 'overallAssessment'],
};
//# sourceMappingURL=coordinator-synthesis.js.map